/**
 * Hook for page-level permission checking
 * Provides consistent 403 handling across all protected pages
 */

import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth-store";
import { Permission } from "@/types";
import { ShieldAlert } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";

interface UsePagePermissionResult {
  hasAccess: boolean;
  isLoading: boolean;
  AccessDenied403: () => JSX.Element;
}

export function usePagePermission(
  requiredPermission: Permission,
): UsePagePermissionResult {
  const navigate = useNavigate();
  const { hasPermission, user, isSuperAdmin } = useAuthStore();

  const hasAccess = isSuperAdmin() || hasPermission(requiredPermission);
  const isLoading = !user; // Loading if user not yet loaded

  const AccessDenied403 = (): JSX.Element => {
    return (
      <div className="-mt-20 flex min-h-screen items-center justify-center">
        <Card className="max-w-md border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody className="py-12 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <ShieldAlert className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-red-900 dark:text-red-100">
              Access Denied
            </h3>
            <p className="mb-2 text-red-700 dark:text-red-300">
              You don't have permission to access this page.
            </p>
            <p className="mb-6 text-sm text-red-600 dark:text-red-400">
              Required permission:{" "}
              <code className="rounded bg-red-100 px-2 py-1 dark:bg-red-900/40">
                {requiredPermission}
              </code>
            </p>
            <button
              onClick={() => navigate({ to: "/admin" })}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-red-700"
            >
              Go to Dashboard
            </button>
          </CardBody>
        </Card>
      </div>
    );
  };

  return {
    hasAccess,
    isLoading,
    AccessDenied403,
  };
}
